// @ts-strict-ignore
import { isNil } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { SortOption } from '@shared/components/filter/types';
import { getIcon } from '@shared/scenes/workflows/ComponentTypeUtils';
import { InputComponent } from '@shared/scenes/workflows/InputComponent';
import { RelatedCardComponent } from '@shared/scenes/workflows/config';
import { useWorkflowDispatch } from '@shared/scenes/workflows/context/context';
import { isInputComponent } from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { v4 } from '@shared/util/uuid';

export function useRelatedCardSort(
  components: API.AnyComponent[],
  component: RelatedCardComponent,
) {
  const dispatch = useWorkflowDispatch();
  const sortByComponents = useMemo(
    () => component?.listDefault?.sortByComponents ?? [],
    [component?.listDefault?.sortByComponents],
  );
  const filterByComponents = component?.listDefault?.filterByComponents ?? [];
  const availableSorts: SortOption[] =
    components
      ?.filter(c => sortByComponents.findIndex(sortBy => sortBy.componentId === c.id) === -1)
      .filter(c => isInputComponent(c) && c.isSortable)
      .map((c: InputComponent) => ({
        id: c.id,
        type: c.type,
        label: c.label,
        icon: getIcon(c),
        component: c,
      })) ?? [];

  const [selectedSorts, setSelectedSorts] = useState<SortOption[]>([]);

  useEffect(() => {
    const selectedSortOptions: SortOption[] = [];
    sortByComponents.forEach(sortBy => {
      const c = components?.find(item => item?.id === sortBy.componentId);
      if (!isNil(c) && isInputComponent(c)) {
        const direction: API.SortDirection = sortBy.direction === 'ASC' ? 'ASC' : 'DESC';
        selectedSortOptions.push({
          uuid: v4(),
          id: c.id,
          type: c.type,
          label: c.label,
          icon: getIcon(c),
          component: c,
          direction,
        });
      }
    });
    setSelectedSorts(selectedSortOptions);
  }, [components, setSelectedSorts, sortByComponents]);

  const onAdd = (item: SortOption) => {
    const newItem = { ...item, uuid: v4() };
    setSelectedSorts([...selectedSorts, newItem]);
    dispatch({
      type: 'updateComponent',
      args: {
        ...component,
        default: { cardIds: [] },
        listDefault: {
          filterByComponents: filterByComponents,
          sortByComponents: [
            ...sortByComponents,
            { componentId: item.id, direction: item.direction === 'ASC' ? 'ASC' : 'DESC' },
          ],
        },
      } as RelatedCardComponent,
    });
  };

  const onUpdate = (item: SortOption) => {
    const newSorts = selectedSorts.map(sort => {
      if (sort.uuid === item.uuid) {
        return { ...item };
      } else {
        return sort;
      }
    });
    setSelectedSorts(newSorts);
    dispatch({
      type: 'updateComponent',
      args: {
        ...component,
        default: { cardIds: [] },
        listDefault: {
          filterByComponents: filterByComponents,
          sortByComponents: newSorts.map(sort => {
            return {
              componentId: sort.id,
              direction: sort.direction === 'ASC' ? 'ASC' : 'DESC',
            };
          }),
        },
      } as RelatedCardComponent,
    });
  };

  const onDelete = (item: SortOption) => {
    const newSorts = selectedSorts.filter(sort => sort.uuid !== item.uuid);
    dispatch({
      type: 'updateComponent',
      args: {
        ...component,
        default: { cardIds: [] },
        listDefault: {
          filterByComponents: filterByComponents,
          sortByComponents: newSorts.map(sort => {
            return {
              componentId: sort.id,
              direction: sort.direction === 'ASC' ? 'ASC' : 'DESC',
            };
          }),
        },
      } as RelatedCardComponent,
    });
  };

  return {
    onAdd,
    onUpdate,
    onDelete,
    selected: selectedSorts,
    available: availableSorts,
  };
}
